import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import Setup2FAPage from "./pages/Setup2FAPage";
import Dashboard from "./pages/Dashboard";
import AdminUsers from "./pages/AdminUsers";
import ProfilePage from "./pages/ProfilePage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ForcePasswordChangePage from "./pages/ForcePasswordChangePage";
import ExpenseManagerApp from "./pages/ExpenseManagerApp";
import AssetManagerApp from "./pages/AssetManagerApp";
import RecruitmentPortalApp from "./pages/RecruitmentPortalApp";
import FormBuilderApp from "./pages/FormBuilderApp";
import PublicFormPage from "./pages/PublicFormPage";
import PayrollUtilitiesApp from "./pages/PayrollUtilitiesApp";
import ProjectsApp from "./pages/ProjectsApp";
import PhishPassApp from "./pages/PhishPassApp";
import PhishCaughtPage from "./pages/PhishCaughtPage";
import AdminSettings from "./pages/AdminSettings";
import TimesheetApp from "./pages/TimesheetApp";
import RunnFillPopup from "./pages/RunnFillPopup";
import PulseApp from "./pages/PulseApp";
import CandorApp from "./pages/CandorApp";
import CandorCandidatePage from "./pages/CandorCandidatePage";
import GrowthApp from "./pages/GrowthApp";
import GrowthSelfAppraisal from "./pages/GrowthSelfAppraisal";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/setup-2fa" element={<Setup2FAPage />} />
          
          {/* Force Password Change Route - allows temporary password users */}
          <Route 
            path="/force-change-password" 
            element={
              <ProtectedRoute allowTemporaryPassword>
                <ForcePasswordChangePage />
              </ProtectedRoute>
            } 
          />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/change-password" 
            element={
              <ProtectedRoute>
                <ChangePasswordPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin Routes */}
          <Route 
            path="/admin/users" 
            element={
              <ProtectedRoute requireSuperAdmin>
                <AdminUsers />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute requireSuperAdmin>
                <AdminSettings />
              </ProtectedRoute>
            }
          />

          {/* Sub-App Routes */}
          <Route 
            path="/apps/expense-manager" 
            element={<ProtectedRoute><ExpenseManagerApp /></ProtectedRoute>} 
          />
          
          <Route 
            path="/apps/asset-manager" 
            element={<ProtectedRoute><AssetManagerApp /></ProtectedRoute>} 
          />
          
          <Route 
            path="/apps/recruitment-portal" 
            element={
              // Signs in from the visitor's own session, so shared links work
              <ProtectedRoute>
                <RecruitmentPortalApp />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/apps/form-builder" 
            element={<ProtectedRoute><FormBuilderApp /></ProtectedRoute>} 
          />
          
          <Route 
            path="/apps/payroll-utilities" 
            element={<ProtectedRoute><PayrollUtilitiesApp /></ProtectedRoute>} 
          />
          
          <Route 
            path="/apps/harmony-projects" 
            element={<ProtectedRoute><ProjectsApp /></ProtectedRoute>} 
          />
          
          <Route 
            path="/apps/phishpass" 
            element={<ProtectedRoute><PhishPassApp /></ProtectedRoute>} 
          />
          
          <Route
            path="/apps/timesheet"
            element={<TimesheetApp />}
          />

          <Route
            path="/apps/harmony-growth"
            element={<GrowthApp />}
          />

          <Route
            path="/apps/harmony-pulse"
            element={<PulseApp />}
          />

          <Route
            path="/apps/candor"
            element={<CandorApp />}
          />

          {/* Public Candor candidate page — reached from the link an
              interviewer sends. The token in the path is the credential. */}
          <Route
            path="/candor/:token"
            element={<CandorCandidatePage />}
          />

          {/* Public self-appraisal — reached from an emailed tokenised link.
              No ProtectedRoute wrap: resources have no Harmony Growth access
              and must be able to complete this without signing in. */}
          <Route
            path="/growth/self-appraisal/:token"
            element={<GrowthSelfAppraisal />}
          />
          
          {/* Public Form Route - No authentication required */}
          <Route
            path="/f/:publicId"
            element={<PublicFormPage />}
          />

          {/* Runn bookmarklet popup — no ProtectedRoute wrap because it must
              be reachable cross-tab to show its own "sign in first" state. */}
          <Route
            path="/runn-fill-popup"
            element={<RunnFillPopup />}
          />
          
          {/* Public PhishPass landing page (after a victim clicks tracked link) */}
          <Route 
            path="/phish/caught/:token" 
            element={<PhishCaughtPage />} 
          />
          
          {/* Default Redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      {/* The app-wide Toaster, mounted on every route. Pages don't mount their
          own: sonner shows each toast in every Toaster that has no id, so it
          would appear twice. For a different placement, give the page's
          Toaster an id and pass that toasterId to its toasts (see
          GrowthSelfAppraisal, PublicFormPage). */}
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
